<template>
  <div class="user-chat-wrapper">
    <div class="user-chat-container">
      <!-- 聊天头部 -->
      <div class="chat-header">
        <div class="header-main">
          <h3>在线客服</h3>
          <div class="header-info">
            <div class="status-indicator" :class="{ online: isCustomerServiceOnline }">
              <el-icon v-if="isCustomerServiceOnline"><SuccessFilled /></el-icon>
              <el-icon v-else><WarningFilled /></el-icon>
              {{ isCustomerServiceOnline ? '客服在线' : '客服离线' }}
              <span v-if="isConnected && isCustomerServiceOnline" class="connection-dot"></span>
            </div>
            <el-button
                v-if="isConnected && isCustomerServiceOnline"
                size="small"
                @click="closeSession"
                class="close-session-btn"
            >
              结束会话
            </el-button>
          </div>
        </div>
      </div>

      <!-- 消息区域 -->
      <div class="chat-messages" ref="messagesContainer">
        <div v-for="(message, index) in messages" :key="message.timestamp + (message.isRead ? '_read' : '_unread')"
             class="message" :class="getMessageClass(message)">
          <div class="message-avatar">
            <el-avatar :size="32" :src="getMessageAvatar(message)">
              <!-- 修改这里：使用图标代替默认显示 -->
              <el-icon v-if="!getMessageAvatar(message)">
                <Avatar/>
              </el-icon>
              <span v-else>{{ getMessageInitial(message) }}</span>
            </el-avatar>
          </div>
          <div class="message-content-wrapper">
            <div class="message-content">{{ message.content }}</div>
            <div class="message-time">
              {{ formatMessageTime(message.sendTime || message.timestamp) }}
              <!-- 已读标识 - 实时更新 -->
              <span v-if="showReadStatus(message, index)" class="read-status">
                {{ getReadStatusText(message) }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="messages.length === 0" class="no-messages">
          <p>暂无消息记录</p>
          <p class="welcome-text">欢迎咨询，我们将竭诚为您服务</p>
        </div>
      </div>

      <!-- 客服在线时的输入区域 -->
      <div class="chat-input-area" v-if="isCustomerServiceOnline && isConnected">
        <div class="input-with-button">
          <el-input
              v-model="inputMessage"
              placeholder="请输入您的问题..."
              @keyup.enter="sendMessage"
              :disabled="!isConnected"
              :rows="3"
              type="textarea"
              resize="none"
              class="message-input"
          />
          <el-button
              @click="sendMessage"
              :disabled="!inputMessage.trim() || !isConnected"
              type="primary"
              class="send-button"
          >
            发送
          </el-button>
        </div>
      </div>

      <!-- 客服离线时的留言区域 -->
      <div v-else-if="!isCustomerServiceOnline" class="offline-message-area">
        <div class="offline-notice">
          客服当前不在线! 请留言，我们上线后会第一时间回复您
        </div>

        <div class="offline-input">
          <el-input
              v-model="offlineMessage"
              type="textarea"
              placeholder="请输入您的留言，建议留下联系方式以便我们回复您..."
              @keyup.enter="sendOfflineMessage"
              :rows="3"
              resize="none"
              class="message-input"
          />
          <el-button
              type="primary"
              @click="sendOfflineMessage"
              :disabled="!offlineMessage.trim()"
              class="send-button"
          >
            发送留言
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted, nextTick, computed} from 'vue'
import {ElMessage, ElMessageBox} from 'element-plus'
import {SuccessFilled, WarningFilled, Avatar} from '@element-plus/icons-vue'
import request from '@/utils/request'

// 用户聊天相关数据
const messages = ref([])
const inputMessage = ref('')
const offlineMessage = ref('')
const isCustomerServiceOnline = ref(false)
const isConnected = ref(false)
const messagesContainer = ref(null)

// 用户和会话信息
const sessionId = ref('')
const visitorId = ref('')
const userId = ref(null)
const userType = ref('VISITOR')
const isPermanentUser = computed(() => userType.value === 'REGISTERED')

// WebSocket相关
let socket = null
let reconnectTimer = null
let heartbeatTimer = null

// 初始化用户聊天
const initUserChat = async () => {
  try {

    // 先检查用户登录状态
    await checkUserLoginStatus();

    const token = localStorage.getItem('auth_token');

    if (token) {
      // 登录用户：创建全新的用户会话，不关联任何访客信息
      await initUserSession();
    } else {
      // 访客：尝试恢复访客会话或创建新会话
      await initVisitorSession();
    }
  } catch (error) {

  }
};


// 初始化用户会话（登录用户）
const initUserSession = async () => {
  try {
    // 清除可能存在的访客会话信息
    sessionStorage.removeItem('im_visitor_id');

    const response = await request.post('/im/session/init');


    if (response.code === '200') {
      sessionId.value = response.data.session.sessionId;
      visitorId.value = null; // 登录用户没有访客ID
      userType.value = 'REGISTERED';
      userId.value = response.data.userId;

      // 存储会话信息到sessionStorage
      sessionStorage.setItem('im_session_id', sessionId.value);
      sessionStorage.setItem('im_user_type', userType.value);
      if (userId.value) {
        sessionStorage.setItem('im_user_id', userId.value.toString());
      }

      // 先检查客服状态，再决定是否连接WebSocket
      await checkCustomerServiceStatus();
      loadHistoryMessages();
    }
  } catch (error) {
    throw error;
  }
};

// 初始化访客会话
const initVisitorSession = async () => {
  try {
    // 尝试从sessionStorage获取现有的访客ID和会话ID
    const storedVisitorId = sessionStorage.getItem('im_visitor_id');
    const storedSessionId = sessionStorage.getItem('im_session_id');

    // 如果有存储的访客ID，使用它来初始化会话
    const visitorIdToUse = storedVisitorId || '';

    // 调用初始化接口，传递访客ID
    const response = await request.post('/im/session/init', null, {
      params: {
        visitorId: visitorIdToUse
      }
    });

    if (response.code === '200') {
      sessionId.value = response.data.session.sessionId;
      visitorId.value = response.data.visitorId;
      userType.value = 'VISITOR';
      userId.value = null;

      // 存储会话信息到sessionStorage
      sessionStorage.setItem('im_session_id', sessionId.value);
      sessionStorage.setItem('im_visitor_id', visitorId.value);
      sessionStorage.setItem('im_user_type', userType.value);

      // 先检查客服状态，再决定是否连接WebSocket
      await checkCustomerServiceStatus();
      loadHistoryMessages();
    }
  } catch (error) {
    console.error('初始化访客会话失败:', error);
    throw error;
  }
};

// 检查用户登录状态
const checkUserLoginStatus = async () => {
  try {
    const response = await request.get('/auth/check-login')
    if (response.code === '200' && response.data.isLoggedIn) {
      userId.value = response.data.userId
      userType.value = 'REGISTERED'
    }
  } catch (error) {

  }
}

// 连接WebSocket
const connectWebSocket = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `ws://localhost:8088/ws`;


  socket = new WebSocket(wsUrl);

  socket.onopen = () => {
    isConnected.value = true


    // 发送用户连接消息
    const connectMsg = {
      type: 'connect',
      sessionId: sessionId.value,
      visitorId: visitorId.value,
      userType: userType.value,
      userId: userId.value
    }
    socket.send(JSON.stringify(connectMsg))


    // 登录用户连接成功后，立即标记所有会话的客服消息为已读
    if (userId.value && isPermanentUser.value) {
      const markReadMsg = {
        type: 'mark_read',
        sessionId: sessionId.value, // 使用当前会话ID，但后端会处理所有会话
        userType: userType.value,
        userId: userId.value,
        timestamp: Date.now(),
        markAllSessions: true // 新增字段，明确表示标记所有会话
      }
      socket.send(JSON.stringify(markReadMsg))

    } else if (sessionId.value) {
      // 访客用户只标记当前会话
      const markReadMsg = {
        type: 'mark_read',
        sessionId: sessionId.value,
        userType: userType.value,
        userId: userId.value,
        timestamp: Date.now()
      }
      socket.send(JSON.stringify(markReadMsg))

    }

    // 连接成功后重新检查客服状态
    checkCustomerServiceStatus();

    // 开始心跳检测
    startHeartbeat()
  }

  socket.onmessage = (event) => {

    const data = JSON.parse(event.data)
    handleWebSocketMessage(data)
  }

  socket.onclose = (event) => {
    isConnected.value = false


    // 清理心跳
    stopHeartbeat()

    // 如果不是正常关闭，尝试重连
    if (event.code !== 1000 && isCustomerServiceOnline.value) {
      scheduleReconnect()
    }
  }

  socket.onerror = (error) => {

    isConnected.value = false
    stopHeartbeat()
  }
}

// 处理WebSocket消息
const handleWebSocketMessage = (data) => {
  switch (data.type) {
    case 'message':
      // 收到客服消息
      console.log('用户收到客服消息:', data);

      const newMessage = {
        content: data.content,
        fromUserType: data.fromUserType,
        sendTime: data.timestamp ? new Date(data.timestamp) : new Date(),
        isRead: false, // 客服消息默认未读，用户查看后才标记为已读
        timestamp: data.timestamp || Date.now(),
        sessionId: data.sessionId
      }
      messages.value.push(newMessage)
      scrollToBottom()

      // 用户收到客服消息后立即标记为已读（因为用户正在查看）
      if (data.fromUserType === 'CUSTOMER_SERVICE' && isConnected.value && sessionId.value) {
        const markReadMsg = {
          type: 'mark_read',
          sessionId: sessionId.value,
          userType: userType.value,
          userId: userId.value,
          timestamp: Date.now()
        }
        socket.send(JSON.stringify(markReadMsg))
        console.log('用户标记客服消息为已读');
      }
      break

    case 'customer_service_online':
      // 处理客服上线事件
      handleCustomerServiceOnline();
      break;

    case 'customer_service_offline':
      // 处理客服下线事件
      handleCustomerServiceOffline();
      break;

    case 'messages_read':
      // 处理消息已读通知
      handleMessagesRead(data);
      break;

    case 'read_status_update':
      // 实时已读状态更新
      handleReadStatusUpdate(data);
      break;

    case 'success':

      if (data.message.includes('留言')) {

      }
      break

    case 'error':

      break
  }
}

// 用户发送消息
const sendMessage = () => {
  if (!inputMessage.value.trim() || !isConnected.value) return

  const message = {
    type: 'message',
    sessionId: sessionId.value,
    content: inputMessage.value.trim(),
    userType: userType.value,
    userId: userId.value,
    timestamp: Date.now()
  }


  socket.send(JSON.stringify(message))

  // 立即在界面显示发送的消息
  messages.value.push({
    content: inputMessage.value.trim(),
    fromUserType: userType.value,
    sendTime: new Date(),
    timestamp: Date.now()
  })

  inputMessage.value = ''
  scrollToBottom()
}

// 发送离线消息
const sendOfflineMessage = async () => {
  if (!offlineMessage.value.trim()) return

  try {
    // 构建离线消息对象
    const offlineMsg = {
      sessionId: sessionId.value,
      content: offlineMessage.value.trim(),
      fromUserType: userType.value,
      fromUserId: userId.value
    }

    // 调用API保存离线消息到数据库
    const response = await request.post('/im/messages/save-offline', offlineMsg)

    if (response.code === '200') {


      // 将留言显示在消息列表中
      messages.value.push({
        content: offlineMessage.value.trim(),
        fromUserType: userType.value,
        sendTime: new Date(),
        timestamp: Date.now(),
        isRead: false
      })

      offlineMessage.value = ''
      scrollToBottom()
    } else {

    }

  } catch (error) {

  }
}

// 关闭会话
const closeSession = async () => {
  try {
    await ElMessageBox.confirm('确定要结束当前会话吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })

    if (socket) {
      // 发送断开连接消息
      const disconnectMsg = {
        type: 'disconnect',
        sessionId: sessionId.value,
        userType: userType.value,
        userId: userId.value
      }
      socket.send(JSON.stringify(disconnectMsg))

      // 关闭WebSocket连接
      socket.close(1000, '用户主动结束会话')
    }

    // 调用后端API结束会话
    await request.post('/im/session/end', null, {
      params: {sessionId: sessionId.value}
    })



    // 清理本地存储
    clearSessionStorage()

    // 重置状态
    resetChatState()
  } catch (error) {
    if (error !== 'cancel') {

    }
  }
}

// 处理客服上线
const handleCustomerServiceOnline = () => {

  isCustomerServiceOnline.value = true;

  // 如果之前没有连接，现在连接WebSocket
  if (!isConnected.value) {
    connectWebSocket();
  }


}

// 处理客服下线
const handleCustomerServiceOffline = () => {

  isCustomerServiceOnline.value = false;

}

// 处理消息已读通知
const handleMessagesRead = (data) => {

  // 客服已读用户的消息，可以更新界面状态
  if (data.readerUserType === 'CUSTOMER_SERVICE') {

  }
}

// 处理实时已读状态更新
const handleReadStatusUpdate = (data) => {

  // 只有当更新的是当前会话时才处理
  if (sessionId.value === data.sessionId) {
    // 更新消息列表中的已读状态
    updateMessagesReadStatus(data)

    // 显示通知
    if (data.readerUserType === 'CUSTOMER_SERVICE') {

    }
  }
}

// 更新消息列表中的已读状态
const updateMessagesReadStatus = (data) => {
  // 找到最后一条用户消息并标记为已读
  for (let i = messages.value.length - 1; i >= 0; i--) {
    const message = messages.value[i]

    // 如果是用户发送的消息且未读，标记为已读
    if (message.fromUserType !== 'CUSTOMER_SERVICE' && !message.isRead) {
      message.isRead = true
      message.readTime = data.timestamp ? new Date(data.timestamp) : new Date()

      // 触发响应式更新
      messages.value.splice(i, 1, {...message})
      break
    }
  }
}

// 检查是否显示已读状态
const showReadStatus = (message, index) => {
  // 只在最后一条消息显示已读状态
  const isLastMessage = index === messages.value.length - 1;

  // 只有用户发送的消息才显示已读状态
  const isUserMessage = message.fromUserType !== 'CUSTOMER_SERVICE';

  return isLastMessage && isUserMessage && message.isRead;
}

// 获取已读状态文本
const getReadStatusText = (message) => {
  if (!message.isRead) return '';
  return '已读';
}

// 检查客服状态
const checkCustomerServiceStatus = async () => {
  try {
    const response = await request.get('/im/customer-service/status')

    if (response.code === '200') {
      const newStatus = response.data

      // 只有当状态发生变化时才更新和显示消息
      if (newStatus !== isCustomerServiceOnline.value) {
        isCustomerServiceOnline.value = newStatus

        if (newStatus && !isConnected.value) {
          // 客服上线且用户未连接，自动连接
          connectWebSocket();

        } else if (!newStatus) {

        }
      }
    }
  } catch (error) {
    console.error('检查客服状态失败:', error)
  }
}

// 加载历史消息
const loadHistoryMessages = async () => {
  try {

    const response = await request.get('/im/history', {
      params: {
        sessionId: sessionId.value
      }
    })

    if (response.code === '200') {
      // 确保消息包含已读状态
      messages.value = response.data.map(msg => ({
        ...msg,
        isRead: msg.isRead !== undefined ? msg.isRead : (msg.fromUserType === 'CUSTOMER_SERVICE' ? false : true)
      }))

      scrollToBottom()
    } else {

    }
  } catch (error) {

  }
}

// 心跳检测
const startHeartbeat = () => {
  heartbeatTimer = setInterval(() => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const heartbeatMsg = {
        type: 'heartbeat',
        sessionId: sessionId.value,
        userType: userType.value,
        userId: userId.value,
        timestamp: Date.now()
      }
      socket.send(JSON.stringify(heartbeatMsg))
    }
  }, 30000) // 30秒一次心跳
}

const stopHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

// 工具函数
const getMessageClass = (message) => {
  return {
    'customer-service': message.fromUserType === 'CUSTOMER_SERVICE',
    'user': message.fromUserType !== 'CUSTOMER_SERVICE',
    // 添加对齐样式
    'message-right': message.fromUserType !== 'CUSTOMER_SERVICE',
    'message-left': message.fromUserType === 'CUSTOMER_SERVICE'
  }
}

// 获取消息头像
const getMessageAvatar = (message) => {
  // 如果消息中有头像且不为空，使用消息头像
  if (message.avatar) {
    return message.avatar;
  }
  // 否则根据用户类型返回 null
  return null;
}

// 获取消息首字母
const getMessageInitial = (message) => {
  return message.fromUserType === 'CUSTOMER_SERVICE' ? 'C' : 'U'
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  if (typeof timestamp === 'string') {
    return new Date(timestamp).toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  return new Date(timestamp).toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 智能格式化消息时间
const formatMessageTime = (timestamp) => {
  if (!timestamp) return ''

  const messageDate = new Date(timestamp)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate())

  // 如果是今天，显示时间
  if (messageDay.getTime() === today.getTime()) {
    return messageDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  // 如果是昨天，显示"昨天 + 时间"
  else if (messageDay.getTime() === yesterday.getTime()) {
    return `昨天 ${messageDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    })}`
  }
  // 如果是更早的时间，显示完整日期和时间
  else {
    return messageDate.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(/\//g, '-')
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

const scheduleReconnect = () => {
  if (reconnectTimer) clearTimeout(reconnectTimer)
  reconnectTimer = setTimeout(() => {

    connectWebSocket()
  }, 3000)
}

const clearSessionStorage = () => {
  sessionStorage.removeItem('im_session_id')
  sessionStorage.removeItem('im_visitor_id')
  sessionStorage.removeItem('im_user_type')
  sessionStorage.removeItem('im_user_id')
}

const resetChatState = () => {
  messages.value = []
  isConnected.value = false
  sessionId.value = ''
  visitorId.value = ''
  userId.value = null
  userType.value = 'VISITOR'
}

// 生命周期
onMounted(() => {
  initUserChat()

  // 每30秒检查一次客服状态（作为WebSocket推送的备用方案）
  const statusCheckInterval = setInterval(() => {
    if (!isCustomerServiceOnline.value) {
      checkCustomerServiceStatus();
    }
  }, 30000);

  // 在组件卸载时清除定时器
  onUnmounted(() => {
    clearInterval(statusCheckInterval);
  });
})

onUnmounted(() => {
  // 组件销毁时清理资源
  stopHeartbeat()

  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
  }

  if (socket) {
    socket.close(1000, '页面关闭')
  }
})
</script>

<style scoped>
.user-chat-wrapper {
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex: 1;
  padding: 20px;
  background: #f5f7fa;
  box-sizing: border-box;
  min-height: calc(100vh - 140px); /* 确保容器不会超过视口高度 */
  max-height: calc(100vh - 120px); /* 添加最大高度限制 */
  overflow: hidden; /* 隐藏容器自身的滚动 */
}

.user-chat-container {
  width: 90%;
  max-width: 1400px;
  background: white;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex: 1; /* 容器也填满父级 */
  min-height: 0; /* 允许收缩 */
}

.chat-header {
  padding: 16px 20px;
  background: #409EFF;
  color: white;
  border-bottom: 1px solid #e4e7ed;
}

.header-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-indicator {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-indicator.online {
  background: #67C23A;
}

.permanent-badge {
  background: rgba(103, 194, 58, 0.2);
  color: #67C23A;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.close-session-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.close-session-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.session-info {
  display: flex;
  gap: 16px;
  font-size: 12px;
  opacity: 0.8;
}

.chat-messages {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: #f5f5f5;

  /* 滚动条样式 */
  scrollbar-width: thin; /* Firefox */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-button {
    display: none;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0,0,0,0.2);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
}

.message {
  display: flex;
  margin-bottom: 20px;
  gap: 5px;
}

/* 用户消息 - 右侧对齐 */
.message.message-right {
  justify-content: flex-end;
}

.message.message-right .message-content-wrapper {
  order: -1; /* 内容在前 */
}

.message.message-right .message-avatar {
  order: 1; /* 头像在后 */
}

.message.message-right .message-content {
  background: #95EC69;
  color: #000;
  border-radius: 8px;
  position: relative;
  margin-right: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* 用户消息气泡的小箭头 */
.message.message-right .message-content::after {
  content: '';
  position: absolute;
  right: -6px;
  top: 10px;
  width: 0;
  height: 0;
  border: 6px solid transparent;
  border-left-color: #95EC69;
  border-right: 0;
}

/* 客服消息 - 左侧对齐 */
.message.message-left {
  justify-content: flex-start;
}

.message.message-left .message-content-wrapper {
  order: 1; /* 内容在后 */
}

.message.message-left .message-avatar {
  order: -1; /* 头像在前 */
}

/* 客服消息气泡 - 左侧，白色 */
.message.message-left .message-content {
  background: #FFFFFF;
  color: #000;
  border-radius: 8px;
  position: relative;
  margin-left: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* 客服消息气泡的小箭头 */
.message.message-left .message-content::before {
  content: '';
  position: absolute;
  left: -6px;
  top: 10px;
  width: 0;
  height: 0;
  border: 6px solid transparent;
  border-right-color: #FFFFFF;
  border-left: 0;
}

/* 消息内容样式调整 */
.message-content-wrapper {
  max-width: 45%;
}

.message.message-right .message-content-wrapper {
  align-items: flex-end;
}

.message.message-left .message-content-wrapper {
  align-items: flex-start;
}

/* 消息气泡样式 */
.message-content {
  padding: 8px 12px;
  border-radius: 8px;
  word-wrap: break-word;
  line-height: 1.4;
  max-width: 100%;
  font-size: 14px;
}

/* 消息时间样式 */
.message-time {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.message.message-right .message-time {
  text-align: right;
}

.message.message-left .message-time {
  text-align: left;
}

.read-status {
  font-size: 11px;
  color: #67c23a;
  margin-left: 8px;
  font-style: italic;
}

.no-messages {
  text-align: center;
  color: #909399;
  padding: 60px 0;
}

.welcome-text {
  margin-top: 8px;
  font-size: 14px;
  color: #606266;
}

.chat-input-area {
  padding: 6px;
  border-top: 1px solid #cdcbcb;
  background: #f5f5f5;
  flex-shrink: 0;
}

.input-with-button {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.message-input {
  /* 移除边框 */
  border: none !important;
  /* 内边距 */
  padding: 12px !important;
}

/* 输入框聚焦时的样式 */
.message-input:deep(.el-textarea__inner) {
  border: none !important;
  background-color: #f5f5f5 !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  resize: none !important;
  /* 设置光标颜色为绿色 */
  caret-color: #07C160 !important;
  font-size: 14px;
  line-height: 1.4;
  min-height: 60px !important;
}

/* 输入框聚焦时的边框颜色 */
.message-input:deep(.el-textarea__inner:focus) {
  border: none !important;
  box-shadow: none !important;
  background-color: #f5f5f5 !important;
}

/* 输入框悬停时的样式 */
.message-input:deep(.el-textarea__inner:hover) {
  border: none !important;

}

/* 禁用状态的样式 */
.message-input:deep(.el-textarea__inner:disabled) {
  background-color: #f9f9f9 !important;
  color: #c0c4cc !important;
}

.send-button {
  height: fit-content;
  white-space: nowrap;
  background-color: #07C160 !important;
  border-color: #07C160 !important;
}

.send-button:hover {
  background-color: #06ae56 !important;
  border-color: #06ae56 !important;
}

.send-button:disabled {
  background-color: #a0a0a0 !important;
  border-color: #a0a0a0 !important;
}

.offline-message-area {
  padding: 20px;
  border-top: 1px solid #cdcbcb;
  background: #f5f5f5;
}

.offline-notice {
  margin-bottom: 10px;
  font-size: 12px;
  color: #e6a23c;
  text-align: center;
}

.offline-input {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.connection-status {
  padding: 8px 16px;

}

/* 添加连接状态指示器 */
.connection-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #67C23A;
  border-radius: 50%;
  margin-left: 6px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>